import { 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query, 
  where,
  Timestamp
} from 'firebase/firestore';
import { db } from '../../firebase';
import { 
  normalizeLicensePlate, 
  licensePlatesMatch, 
  generatePlateVariations,
  convertArabicToEnglish 
} from '../../utils/arabic-english-converter';

class VehicleMappingService {
  constructor() {
    this.collectionName = 'vehicle_mappings';
  }

  // Get all vehicle mappings for a company
  async getVehicleMappings(companyId) {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('companyId', '==', companyId)
      );
      
      const querySnapshot = await getDocs(q);
      const mappings = {};
      
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        mappings[data.petroAppVehicle] = data.systemVehiclePlate;
      });
      
      return mappings;
    } catch (error) {
      console.error('Error getting vehicle mappings:', error);
      throw error;
    }
  }

  // Save a vehicle mapping
  async saveVehicleMapping(companyId, petroAppVehicle, systemVehiclePlate, userId) {
    try {
      const mappingId = `${companyId}_${petroAppVehicle.replace(/\s+/g, '_')}`;
      const docRef = doc(db, this.collectionName, mappingId);
      
      await setDoc(docRef, {
        companyId,
        petroAppVehicle,
        systemVehiclePlate,
        createdBy: userId,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      
      return true;
    } catch (error) {
      console.error('Error saving vehicle mapping:', error);
      throw error;
    }
  }

  // Update a vehicle mapping
  async updateVehicleMapping(companyId, petroAppVehicle, systemVehiclePlate, userId) {
    try {
      const mappingId = `${companyId}_${petroAppVehicle.replace(/\s+/g, '_')}`;
      const docRef = doc(db, this.collectionName, mappingId);
      
      await updateDoc(docRef, {
        systemVehiclePlate,
        updatedBy: userId,
        updatedAt: Timestamp.now()
      });
      
      return true;
    } catch (error) {
      console.error('Error updating vehicle mapping:', error);
      throw error;
    }
  }

  // Delete a vehicle mapping
  async deleteVehicleMapping(companyId, petroAppVehicle) {
    try {
      const mappingId = `${companyId}_${petroAppVehicle.replace(/\s+/g, '_')}`;
      const docRef = doc(db, this.collectionName, mappingId);
      
      await deleteDoc(docRef);
      
      return true;
    } catch (error) {
      console.error('Error deleting vehicle mapping:', error);
      throw error;
    }
  }

  // Find potential matches between PetroApp vehicle and system vehicles
  findPotentialMatches(petroAppVehicle, systemVehicles) {
    const matches = [];

    for (const vehicle of systemVehicles) {
      // Check if plates match using Arabic-English aware matching
      if (licensePlatesMatch(petroAppVehicle, vehicle.plateNumber)) {
        matches.push({
          vehicle,
          similarity: 1.0,
          reason: 'Arabic-English plate match'
        });
      } else {
        // Calculate similarity using the enhanced method
        const similarity = this.calculateEnhancedSimilarity(petroAppVehicle, vehicle.plateNumber);
        
        if (similarity > 0.3) { // 30% similarity threshold
          matches.push({
            vehicle,
            similarity,
            reason: this.getMatchReason(petroAppVehicle, vehicle.plateNumber)
          });
        }
      }
    }

    // Sort by similarity score (highest first)
    return matches.sort((a, b) => b.similarity - a.similarity);
  }

  // Clean vehicle name for comparison (now uses Arabic-English aware normalization)
  cleanVehicleName(name) {
    return normalizeLicensePlate(name);
  }

  // Enhanced similarity calculation with Arabic-English support
  calculateEnhancedSimilarity(plate1, plate2) {
    if (!plate1 || !plate2) return 0;
    
    // Generate all variations for both plates
    const variations1 = generatePlateVariations(plate1);
    const variations2 = generatePlateVariations(plate2);
    
    let maxSimilarity = 0;
    
    // Compare all variations and find the highest similarity
    for (const v1 of variations1) {
      for (const v2 of variations2) {
        const similarity = this.calculateSimilarity(v1, v2);
        maxSimilarity = Math.max(maxSimilarity, similarity);
      }
    }
    
    return maxSimilarity;
  }

  // Calculate similarity between two strings
  calculateSimilarity(str1, str2) {
    if (!str1 || !str2) return 0;
    
    // Exact match
    if (str1 === str2) return 1.0;
    
    // Check if one contains the other
    if (str1.includes(str2) || str2.includes(str1)) {
      return 0.8;
    }
    
    // Check for common subsequences
    const commonChars = this.getCommonCharacters(str1, str2);
    const maxLength = Math.max(str1.length, str2.length);
    
    if (maxLength === 0) return 0;
    
    return commonChars / maxLength;
  }

  // Get common characters between two strings
  getCommonCharacters(str1, str2) {
    let common = 0;
    const shorter = str1.length < str2.length ? str1 : str2;
    const longer = str1.length >= str2.length ? str1 : str2;
    
    for (let i = 0; i < shorter.length; i++) {
      if (longer.includes(shorter[i])) {
        common++;
      }
    }
    
    return common;
  }

  // Get reason for match (enhanced with Arabic-English awareness)
  getMatchReason(petroApp, systemPlate) {
    // Check for Arabic-English conversion match
    if (licensePlatesMatch(petroApp, systemPlate)) {
      const normalizedPetro = normalizeLicensePlate(petroApp);
      const normalizedSystem = normalizeLicensePlate(systemPlate);
      
      if (normalizedPetro === normalizedSystem) {
        return 'Exact match';
      } else {
        return 'Arabic-English conversion match';
      }
    }
    
    const cleanPetro = this.cleanVehicleName(petroApp);
    const cleanSystem = this.cleanVehicleName(systemPlate);
    
    if (cleanPetro === cleanSystem) {
      return 'Exact match (normalized)';
    }
    
    if (cleanPetro.includes(cleanSystem) || cleanSystem.includes(cleanPetro)) {
      return 'Contains match';
    }
    
    const similarity = this.calculateEnhancedSimilarity(petroApp, systemPlate);
    
    if (similarity > 0.7) {
      return 'High similarity';
    } else if (similarity > 0.5) {
      return 'Medium similarity';
    } else {
      return 'Low similarity';
    }
  }

  // Apply vehicle mappings to fuel transactions
  applyVehicleMappings(transactions, mappings) {
    return transactions.map(transaction => ({
      ...transaction,
      mappedVehiclePlate: mappings[transaction.vehicle_plate] || transaction.vehicle_plate
    }));
  }
}

export const vehicleMappingService = new VehicleMappingService();